/**
 * OnboardingGateContext — 3-stage progressive dashboard lock (2026-09-05).
 *
 * Stage order for a retail trader (role='user'):
 *   1. "profile"      — profile completion score < 100%.
 *   2. "strategy"     — profile 100%, Investment Strategy questionnaire not
 *                        yet completed. Applies to EVERY tier — mirrors the
 *                        existing hard backend gate on order placement
 *                        (`services/order_middleware.py::ensure_investment_
 *                        strategy_completed`, all non-admin tiers).
 *   3. "psychometric"  — strategy done, Psychometric Baseline not yet
 *                        recorded. Only applies to institutional-lite tiers
 *                        (advanced/moe/cma_institution) — mirrors the
 *                        existing `is_institutional_lite` paywall, so free
 *                        tier never sees this stage.
 *   "complete"         — full dashboard unlocked.
 *
 * Admins/brokers are exempt (their own layouts have separate guards).
 */
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasInstitutionalFeatures } from "@/lib/tier";

const OnboardingGateContext = createContext({ stage: "complete", profilePct: 100 });

// Routes still reachable while a given stage is active — everything else
// bounces back to /app so the lock can't be sidestepped by direct URL entry.
export const GATE_ALLOWED_PATHS = {
    profile: ["/app", "/app/settings"],
    strategy: ["/app", "/onboarding/investment-strategy"],
    psychometric: ["/app", "/onboarding/psychometric"],
};

export function OnboardingGateProvider({ children }) {
    const { user } = useAuth();
    const [profilePct, setProfilePct] = useState(null);

    // Bug fix (2026-09-06): this only re-fetched on `user?.id`, which never
    // changes when a profile field is saved — ProfileEditModal calls the
    // auth context's `refresh()` (which DOES update user.phone/nationality/
    // country_of_residence/is_email_verified on the user object), but this
    // effect never re-ran, so `profilePct`/`stage` stayed stuck at the OLD
    // value forever. The dashboard below stays `pointer-events-none` while
    // stage !== "complete", so this looked exactly like a full page freeze
    // even after the user had finished every required field. Depending on
    // the actual fields that feed /profile/completion's score makes this
    // re-fetch — and the dashboard unlock — reactive again.
    useEffect(() => {
        if (!user || user.role !== "user") {
            setProfilePct(null);
            return;
        }
        let cancelled = false;
        api.get("/profile/completion")
            .then(({ data }) => { if (!cancelled) setProfilePct(data.score); })
            // Fail-open — a network blip must never lock a trader out.
            .catch(() => { if (!cancelled) setProfilePct(100); });
        return () => { cancelled = true; };
    }, [user?.id, user?.phone, user?.nationality, user?.country_of_residence, user?.is_email_verified]);

    let stage = "complete";
    if (user && user.role === "user") {
        if (profilePct == null) {
            stage = "complete"; // still resolving — don't flash a lock/redirect
        } else if (profilePct < 100) {
            stage = "profile";
        } else if (!user.has_completed_investment_strategy) {
            stage = "strategy";
        } else if (hasInstitutionalFeatures(user) && user.psychometric_composite == null) {
            stage = "psychometric";
        }
    }

    return (
        <OnboardingGateContext.Provider value={{ stage, profilePct }}>
            {children}
        </OnboardingGateContext.Provider>
    );
}

export function useOnboardingGate() {
    return useContext(OnboardingGateContext);
}
